<script>
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-empty-function */
import { defineComponent, ref, nextTick, getCurrentInstance, reactive, computed } from '@vue/composition-api'
import { clone, isEmpty, head } from 'lodash'
import useModal from '@/hooks/useModal'
import useMlForm from '@/hooks/useMlForm'
import Upload from './Upload'
import {
  GALLERY_API,
  IMAGE_API,
  GALLERY_SCHEMA,
  IMAGE_SCHEMA,
  confirmDelete,
  fileToUrl,
  success,
  error,
} from './shared'

const STEP = 20

export default defineComponent({
  components: {
    Upload,
  },
  setup(_, { root }) {
    const gallery = reactive({
      list: [],
      current: { id: 'create' },
      active: {},
      schema: GALLERY_SCHEMA,
      api: GALLERY_API,
    })

    const image = reactive({
      list: [],
      checked: [],
      active: null,
      schema: IMAGE_SCHEMA,
      api: IMAGE_API,
      isEditable: computed(() => !isEmpty(image.checked)),
      isUploadable: computed(() => gallery.current.id !== 'create'),
      cache: new Map(),
      size: STEP,
      total: 0,
      noMore: computed(() => image.size > image.total),
      disabled: computed(() => image.loading || image.noMore),
      loading: false,
      key: 0
    })

    const modal = reactive({
      type: 'galleryCreate',
      ...useModal({ okText: root.$t('submit' || '提交') }),
    })
    const formRef = ref()

    function onGalleryMenuClick({ key }) {
      if (key === 'create') {
        openModal('galleryCreate')
        return
      }
      if (key === gallery.current.id) return
      gallery.current = gallery.list.find(item => item.id === key)
      image.size = STEP
      image.key = Math.random() * 10000
      // debugger
      handleImageRead()
    }

    function onGalleryRenameOrDelete({ key }, record) {
      if (key === 'rename') {
        gallery.active = record
        openModal('galleryRename')
      } else if (key === 'delete') handleGalleryDelete(record.id)
    }

    async function handleGalleryRead() {
      try {
        const res = await gallery.api.read()
        gallery.list = res.data
        if (!isEmpty(gallery.list)) {
          gallery.current = head(gallery.list)
          handleImageRead()
        } else {
          gallery.current = { id: 'create' }
        }
      } catch (e) {}
    }

    async function handleGalleryCreate() {
      try {
        modal.showLoading()
        await formRef.value.validate()
        await gallery.api.create(formRef.value.formAttrs.model)
        modal.hideLoading()
        success()
        modal.close()
        const res = await gallery.api.read()
        gallery.list = res.data
        if (!isEmpty(gallery.list)) {
          gallery.current = head(gallery.list)
          handleImageRead()
        }
      } catch (e) {
        modal.hideLoading()
        throw new Error(e)
      }
    }

    async function handleGalleryRename() {
      try {
        modal.showLoading()
        await formRef.value.validate()
        await gallery.api.rename(formRef.value.formAttrs.model)
        modal.hideLoading()
        success()
        modal.close()
        const res = await gallery.api.read()
        gallery.list = res.data
        if (!isEmpty(gallery.list)) {
          gallery.current = head(gallery.list)
          handleImageRead()
        }
      } catch (e) {
        modal.hideLoading()
        throw new Error(e)
      }
    }

    async function handleGalleryDelete(id) {
      if (await confirmDelete(gallery.api.delete, id)) {
        handleGalleryRead()
      }
    }

    async function handleImageRead() {
      image.checked = []
      if (!gallery.current.id) return
      try {
        image.loading = true
        const res = await image.api.read(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          gallery.current.id,
          undefined,
          undefined,
          undefined,
          image.size
        )
        image.list = res.data.records.map(item => ({ ...item, url: '', error: false, loading: false }))
        image.total = res.data.total
        fileToUrl(image.list, image.cache)
      } catch (e) {
        throw new Error(e)
      } finally {
        image.loading = false
      }
    }

    async function handleImageUpload() {
      try {
        modal.showLoading()
        await formRef.value.validate()

        const dto = {
          galleryId: formRef.value.formAttrs.model.galleryId,
          file: formRef.value.formAttrs.model.file.map(item => ({
            contentType: item.contentType,
            fileName: item.originalFilename,
            objectKey: item.objectKey,
          })),
        }
        await image.api.upload(dto)
        modal.hideLoading()
        success()
        modal.close()
        handleImageRead()
      } catch (e) {
        modal.hideLoading()
        throw new Error(e)
      }
    }

    async function handleImageDelete() {
      if (await confirmDelete(image.api.delete, image.checked.join(','))) {
        handleImageRead()
      }
    }

    async function handleImageRename() {
      try {
        await image.api.rename({ ...image.active })
        success()
        image.active = null
      } catch (e) {}
    }

    async function handleImageMove() {
      try {
        modal.showLoading()
        await formRef.value.validate()
        await image.api.move(formRef.value.formAttrs.model)
        modal.hideLoading()
        success()
        modal.close()
        handleImageRead()
      } catch (e) {
        modal.hideLoading()
        throw new Error(e)
      }
    }

    function onCheckChange(checked) {
      image.checked = checked
    }

    function openModal(type) {
      if (type === 'galleryCreate') {
        modal.modalEvents.ok = handleGalleryCreate
      }
      if (type === 'galleryRename') {
        gallery.schema.rename[0].ui.initialValue = gallery.active.galleryName
        gallery.schema.rename[1].ui.initialValue = gallery.active.id
        modal.modalEvents.ok = handleGalleryRename
      }
      if (type === 'imageUpload') {
        image.schema.upload[1].ui.initialValue = gallery.current.id
        modal.modalEvents.ok = handleImageUpload
      }
      if (type === 'imageMove') {
        image.schema.move[0].ui.enum = gallery.list.map(item => ({ label: item.galleryName, value: item.id }))
        image.schema.move[1].ui.initialValue = image.checked.join(',')
        modal.modalEvents.ok = handleImageMove
      }
      const typeMap = {
        galleryCreate: 'create' || '新建',
        galleryRename: 'page_role_management_rename' || '重命名',
        imageUpload: 'upload' || '上传',
        imageMove: 'page_my_document_move' || '移动',
      }
      modal.modalAttrs.title = root.$t(typeMap[type])
      modal.type = type
      modal.modalAttrs.visible = true
    }

    function startRenameImage(record) {
      image.active = record
    }

    function endRenameImage() {
      image.active = null
    }

    function onImageScroll(e) {
      // let dom = e.target
      // //文档内容实际高度（包括超出视窗的溢出部分）
      // let scrollHeight = Math.max(dom.scrollHeight, dom.scrollHeight)
      // //滚动条滚动距离
      // let scrollTop = e.target.scrollTop
      // //窗口可视范围高度
      // let clientHeight = dom.innerHeight || Math.min(dom.clientHeight, dom.clientHeight)
      // if (clientHeight + scrollTop >= scrollHeight) {
      //   if (image.size > image.total) return
      //   image.size += 10
      //   handleImageRead()
      // }
    }

    function load() {
      console.log('load')
      if (image.noMore) return
      console.log('real load')
      image.size += STEP
      handleImageRead()
    }

    // init
    handleGalleryRead()

    return {
      gallery,
      image,
      modal,

      formRef,

      onGalleryMenuClick,
      onGalleryRenameOrDelete,

      handleGalleryRead,
      handleGalleryCreate,
      handleGalleryRename,
      handleGalleryDelete,

      handleImageRead,
      handleImageUpload,
      handleImageDelete,
      handleImageRename,
      handleImageMove,

      onCheckChange,
      openModal,
      startRenameImage,
      endRenameImage,
      onImageScroll,

      load,
    }
  },
})
</script>

<template>
  <div class="container">
    <a-menu
      :selectedKeys="[gallery.current.id]"
      class="pb-4"
      style="width: 240px; height: 100%; overflow-y: auto;"
      @click="onGalleryMenuClick"
    >
      <a-menu-item
        v-for="item of gallery.list"
        :key="item.id"
        style="position: relative; height: 60px; line-height: 60px; font-size: 16px;"
      >
        {{ $t(item.galleryName) }}
        <a-dropdown>
          <template #overlay>
            <a-menu @click="onGalleryRenameOrDelete($event, item)">
              <a-menu-item key="rename">{{ $t('page_role_management_rename' || '重命名') }}</a-menu-item>
              <a-menu-item key="delete">{{ $t('delete' || '删除') }}</a-menu-item>
            </a-menu>
          </template>
          <a-icon type="down" style="position: absolute; right: 0; top: 50%; transform: translateY(-50%)" />
        </a-dropdown>
      </a-menu-item>
      <a-menu-item
        style="height: 60px; line-height: 60px; text-align: center; border: 1px solid #eee; margin: 8px 12px;"
        key="create"
      >
        <a-icon type="plus" />{{ $t('page_console_gallery_create' || '新建图库') }}
      </a-menu-item>
    </a-menu>

    <div class="p-4" style="flex: 1">
      <div>
        <a-button type="primary" :disabled="!image.isUploadable" @click="openModal('imageUpload')">{{
          $t('upload' || '上传')
        }}</a-button>
        <a-button class="ml-4" :disabled="!image.isEditable" @click="openModal('imageMove')">{{
          $t('page_my_document_move' || '移动')
        }}</a-button>
        <a-button class="ml-4" type="danger" :disabled="!image.isEditable" @click="handleImageDelete">{{
          $t('delete' || '删除')
        }}</a-button>
      </div>
      <ul
        v-infinite-scroll="load"
        :infinite-scroll-disabled="false"
        class="pt-4"
        style="display: flex; flex-wrap: wrap; overflow-y: auto; height: calc(100% - 32px);"
        @scroll="onImageScroll"
      >
        <a-checkbox-group style="width: 100%;" @change="onCheckChange" :key="image.key">
          <li v-for="item of image.list" :key="item.id" class="mb-10" style="position: relative; display: inline;">
            <a-checkbox style="margin-bottom: 40px;" :value="item.id">
              <div style="display: inline-block; width: 200px; height: 200px; vertical-align: middle">
                <div
                  style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; position: relative;"
                >
                  <template v-if="item.error">
                    <div
                      style="width: 100%; height: 100%; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: #eee; color: #999; display: flex; justify-content: center; align-items: center"
                    ></div>
                  </template>
                  <template v-else>
                    <template v-if="item.loading">
                      <a-spin /> 
                    </template>
                    <template v-else>
                      <img style="max-width: 200px; max-height: 200px;" :src="item.url" />
                    </template>
                  </template>
                </div>
              </div>
            </a-checkbox>
            <div
              v-if="image.active === item"
              style="display: flex; align-items: center; width: 160px; position: absolute; left: 50%; bottom: -125px; transform: translate(-50%, 0)"
            >
              <a-input v-model="image.active.fileName" style="margin-right: 8px;"></a-input>
              <a style="white-space: nowrap" @click="handleImageRename">{{ $t('save' || '保存') }}</a>
              <a-divider type="vertical"></a-divider>
              <a style="white-space: nowrap" @click="endRenameImage">{{ $t('cancel' || '取消') }}</a>
            </div>
            <a-dropdown v-else>
              <template #overlay>
                <a-menu style="width: 80px;" @click="startRenameImage(item)">
                  <a-menu-item>{{ $t('page_role_management_rename' || '重命名') }}</a-menu-item>
                </a-menu>
              </template>
              <span style="position: absolute; left: 50%; bottom: -120px; transform: translate(-50%, 0)">
                <ellipsis tooltip :length="10">{{ item.fileName }}</ellipsis>
              </span>
            </a-dropdown>
          </li>
        </a-checkbox-group>
      </ul>
      <!-- <p v-if="image.loading" style="text-align: center;">加载中...</p>
      <p v-if="image.noMore" style="text-align: center;">没有更多了</p> -->
    </div>

    <a-modal v-bind="modal.modalAttrs" v-on="modal.modalEvents">
      <template v-if="modal.type === 'galleryCreate'">
        <ml-form ref="formRef" :schema="gallery.schema.create"></ml-form>
      </template>
      <template v-if="modal.type === 'galleryRename'">
        <ml-form ref="formRef" :schema="gallery.schema.rename"></ml-form>
      </template>
      <template v-if="modal.type === 'imageUpload'">
        <ml-form ref="formRef" :schema="image.schema.upload">
          <template #image-upload="{ model }">
            <Upload v-model="model.file"></Upload>
          </template>
        </ml-form>
      </template>
      <template v-if="modal.type === 'imageMove'">
        <ml-form ref="formRef" :schema="image.schema.move"></ml-form>
      </template>
    </a-modal>
  </div>
</template>

<style scoped lang="less">
.container {
  display: flex;
  width: 100%;
  height: 100%;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
}
</style>
